import { IOpenedAppModal } from "@mctypes/frontend.types";
import { IBoardBucketDeprecated, IProjectDeprecated } from "@mctypes/global.types";
import { IIssueActionNameOption, IIssueRegisterNameOption } from "@mctypes/issue.types";
import { createSelector } from "@reduxjs/toolkit";
import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { ICountersForAllAttributesWithFormula, IIssueApi } from "@schemas/issueSchema";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import MiniSearch from "minisearch";
import { GetProjectByProjectIdIssuesOnBoardApiArg, openapi } from "../../../../openapi";
import { AppModalEnums } from "../../../mc-shared/sharedConstants";
import { AccessLevelsEnum, IssueDisabledColumnsEnum } from "../../../mc-shared/enums";
import { getIssuesWithFormulaAdded } from "../../../mc-shared/sharedUtils";
import { IRootState } from "../../../store";
import { STANDARD_COLORS } from "../../globalConstants";
import { selectHttpProjectMembers, selectProjectNo } from "../../projectSelectors";
import { sortArrayByNumber } from "../../utils";
import { selectHttpFavoriteIssueboardIds } from "./issueBoardFavoritesSelector";
import { issueBoardRTK } from "./IssueBoardRTK";
import { getIssuesWithMathFormulaAdded } from "./issueMathFormulaUtils";
import { validateMathFormulaExpression } from "../../../components/OtherAttributes/OtherAttributesConfig/MathFormulaConfig/mathFormulaConfigUtils";
import {
  ISSUE_ACTION_NAME_OPTIONS,
  DEFAULT_VISIBLE_ISSUE_COLUMNS,
  ISSUE_COLUMN_DATAFIELDS_ARRAY,
  ISSUE_REGISTER_NAME_OPTIONS,
  ISSUE_STATES_OPTIONS,
  IssueKanbanGroupingViewEnum,
  ISSUE_DATAFILEDS_ENUM,
} from "./issueConstants";
import { IAgChartDataItem } from "./IssuesTab/Dashboard/StatusCircleChart";
import { ENRICHERS, IEnrichmentContext } from "./IssuesTab/issueEnrichers";
import { convertIssuesToKanbanBoardColumns, filterAllIssues } from "./issueUtils";
import { selectIssueBoardId } from "./redux/selectors/issueCommonSelectors";
import { selectHttpIssueBoard } from "./redux/selectors/issueRtkSelectors";
import { IKanbanColumn } from "./types";

const selectCurrentProjectId = (state: IRootState) => state.globalReducer.currentProjectId;
const selectedPnsBoardFilterItem = (state: IRootState) => state.issueReducerNew.selectedPnsBoardFilterItem;
const selectIssueDropdownFilters = (state: IRootState) => state.issueReducerNew.dropdownFilters;
const selectMiniSearchFreeText = (state: IRootState) => state.issueReducerNew.miniSearchFreeText;
const selectPnsFilter = (state: IRootState) => state.issueReducerNew.selectedPnsFilterItem;
const selectTaskAndOrIssueArgs = (state: IRootState) => state.issueReducerNew.taskAndOrIssueArgs;
const selectKanbanGroupingView = (state: IRootState) => state.issueReducerNew.kanbanGroupingView;

const EMPTY_ISSUES: IIssueApi[] = [];
const EMPTY_BUCKETS: IBoardBucketDeprecated[] = [];
const EMPTY_FORMULAS: ICountersForAllAttributesWithFormula = {};

export const selectGetOpenIssueArgsModalSelector = createSelector(
  [
    (_state: IRootState, _id: string) => _id,
    (state: IRootState) => state.issueReducerNew.currentIssueBoardId,
    (state: IRootState) => state.globalReducer.currentProjectId,
  ],
  (_id, boardId, projectId): IOpenedAppModal => {
    return {
      itemId: _id,
      boardId,
      projectId,
      app: AppModalEnums.ISSUE_MANTINE,
    };
  },
);

const selectCurrentIssueBoardId = (state: IRootState) => state.issueReducerNew.currentIssueBoardId;

export const selectIssuesOrTasksOnBoardArgs = createSelector(
  [selectCurrentProjectId, selectCurrentIssueBoardId, selectTaskAndOrIssueArgs],
  (projectId, issueBoardId, taskAndOrIssueArgs): GetProjectByProjectIdIssuesOnBoardApiArg => ({
    projectId,
    issueboardId: issueBoardId,
    includeIssues: taskAndOrIssueArgs.includes("ISSUE") ? "true" : "false",
    includeTasks: taskAndOrIssueArgs.includes("TASK") ? "true" : "false",
  }),
);

export const selectHttpIssuesOnBoard = createSelector(
  [(state: IRootState) => state, selectIssuesOrTasksOnBoardArgs],
  (state, args) => {
    if (!args) return EMPTY_ISSUES;
    const q = openapi.endpoints.getProjectByProjectIdIssuesOnBoard.select(args)(state);
    return q.data ?? EMPTY_ISSUES;
  },
);

export const selectCurrentOpenIssueModal = createSelector(
  [(state: IRootState) => state.globalReducer.openedAppModals],
  (openedAppModals): IOpenedAppModal | null =>
    openedAppModals
      .slice()
      .reverse()
      .find((modal) => modal.app === AppModalEnums.ISSUE || modal.app === AppModalEnums.ISSUE_MANTINE) ?? null,
);

export const selectCurrentOpenIssueInModal = createSelector(
  [(state: IRootState) => state, selectCurrentOpenIssueModal],
  (state, modal): IIssueApi | null => {
    if (modal?.projectId == null || modal.itemId == null) return null;

    return (
      openapi.endpoints.getProjectByProjectIdIssuesGetSingle.select({
        projectId: modal.projectId,
        issueId: modal.itemId,
      })(state).data ?? null
    );
  },
);

export const selectCurrentOpenIssueBoardInModal = createSelector(
  [(state: IRootState) => state, selectCurrentOpenIssueModal],
  (state, modal): IIssueBoardApi | null => {
    if (modal?.projectId == null || modal.boardId == null) return null;

    return (
      issueBoardRTK.endpoints.getSingleIssueBoard.select({
        projectId: modal.projectId,
        issueBoardId: modal.boardId,
      })(state).data ?? null
    );
  },
);

export const selectGroupedIssuesOnBoardByCategory = createSelector([selectHttpIssuesOnBoard], (issues) => {
  const groupedIssues = issues.reduce<Record<string, { id: string; title: string; items: IIssueApi[] }>>(
    (acc, issue) => {
      const parent = issue.connectedIssue;

      if (!parent) return acc;

      if (!acc[parent._id]) {
        acc[parent._id] = {
          id: parent._id,
          title: parent.title,
          items: [],
        };
      }

      acc[parent._id].items.push(issue);
      return acc;
    },
    {},
  );
  return groupedIssues;
});

const EMPTY_ISSUE_BOARDS = [];
export const selectHttpIssueBoards = createSelector(
  [(state: IRootState) => state, selectCurrentProjectId],
  (state, projectId): IIssueBoardApi[] => {
    if (!projectId) return EMPTY_ISSUE_BOARDS;
    const q = issueBoardRTK.endpoints.getIssueBoards.select({ projectId })(state);
    return q.data ?? EMPTY_ISSUE_BOARDS;
  },
);

export const selectIssueBoardsWithFavoriteRows = createSelector(
  [selectHttpIssueBoards, selectHttpFavoriteIssueboardIds, selectedPnsBoardFilterItem],
  (issueBoards, favoriteIssueboardIds, pnsBoardFilterItem) => {
    return issueBoards
      .map((board) => ({
        ...board,
        isFavorite: favoriteIssueboardIds.find((fav) => fav.issueboard === board._id) != null,
      }))
      .filter((board) => {
        if (!pnsBoardFilterItem) return true;
        return board.pns?.startsWith(pnsBoardFilterItem);
      });
  },
);

export const selectHttpSumsByFormula = createSelector(
  [(state: IRootState) => state, selectCurrentProjectId, selectIssueBoardId],
  (state, projectId, issueBoardId) => {
    if (!projectId || !issueBoardId) return EMPTY_FORMULAS;
    const q = issueBoardRTK.endpoints.getSumsByFormula.select({ projectId, issueBoardId })(state);
    return q.data ?? EMPTY_FORMULAS;
  },
);

export const selectIssueBoardContainsFormula = createSelector(
  [selectHttpIssueBoard],
  (issueBoard: IIssueBoardApi | null) => {
    return (
      Object.values(issueBoard?.otherAttributesConfig ?? {}).find(
        (attribute) => attribute.typeOfValue === "FORMULA" && attribute.isActive,
      ) != null
    );
  },
);

export const selectConnectedBimSpacesDisabled = createSelector([selectHttpIssueBoard], (issueBoard) => {
  const disabledColumns = issueBoard?.disabledColumns ?? [];
  return disabledColumns.includes(IssueDisabledColumnsEnum.CONNECTEDBIMSPACES);
});

export const selectConnectedBimSpacesColumnActive = createSelector(
  [(state: IRootState) => state.issueReducerNew.allColumnState, selectIssueBoardId],
  (allColumnState, issueBoardId) => {
    const visibleColumns =
      issueBoardId != null && allColumnState[issueBoardId]?.length > 0
        ? allColumnState[issueBoardId].map((col) => col.colId)
        : DEFAULT_VISIBLE_ISSUE_COLUMNS;

    return visibleColumns.includes(ISSUE_DATAFILEDS_ENUM.CONNECTEDBIMSPACES);
  },
);

export const selectIssuesWithFormulaAdded = createSelector(
  [selectHttpIssuesOnBoard, selectHttpIssueBoard, selectHttpSumsByFormula, selectIssueBoardContainsFormula],
  (issues, issueBoard, sumsByFormula, issueBoardContainsFormula) => {
    const issuesWithClassicFormula = issueBoardContainsFormula
      ? getIssuesWithFormulaAdded({
          issues,
          issueBoard,
          sumsByFormula,
        })
      : issues;

    if (!issueBoard) {
      return issuesWithClassicFormula;
    }

    return getIssuesWithMathFormulaAdded({
      issues: issuesWithClassicFormula,
      issueBoard,
    });
  },
);

export const selectMiniIndexForIssues = createSelector(
  [selectIssuesWithFormulaAdded],
  (issues): MiniSearch<IIssueApi> => {
    const mini = new MiniSearch<IIssueApi>({
      fields: ISSUE_COLUMN_DATAFIELDS_ARRAY,
      idField: "_id",
      searchOptions: {
        boost: { title: 3 },
        fuzzy: 0.2,
        prefix: true,
      },
    });

    if (issues.length) {
      mini.removeAll();
      mini.addAll(issues);
    }
    return mini;
  },
);

export const selectIssuesWithAllEnrichedData = createSelector(
  [
    selectHttpIssuesOnBoard,
    selectHttpProjectMembers,
    selectHttpIssueBoard,
    selectHttpSumsByFormula,
    selectIssueBoardContainsFormula,
  ],
  (rawIssues, projectMembers, issueBoard, sumsByFormula, hasFormula): IIssueApi[] => {
    // Om ingen data → tidig return
    if (!rawIssues.length) return rawIssues;

    // Bygg context
    const context: IEnrichmentContext = {
      memberMap: new Map(),
      formulaContext: hasFormula ? { issueBoard: issueBoard!, sumsByFormula } : undefined,
    };

    // Bygg memberMap
    for (const pm of projectMembers) {
      context.memberMap.set(pm.user._id.toString(), pm);
    }

    // Kör pipeline på varje issue
    const enrichedIssues = rawIssues.map((issue) => ENRICHERS.reduce((acc, enrich) => enrich(acc, context), issue));

    if (!issueBoard) {
      return enrichedIssues;
    }

    return getIssuesWithMathFormulaAdded({
      issues: enrichedIssues,
      issueBoard,
    });
  },
);

const selectLoggedInUser = (s: IRootState) => s.globalReducer.user;

export const selectFilteredIssuesOnBoard = createSelector(
  [
    selectIssuesWithAllEnrichedData, // ← NU har vi ALLT!
    selectIssueDropdownFilters,
    selectMiniSearchFreeText,
    selectPnsFilter,
    selectMiniIndexForIssues,
    selectLoggedInUser,
  ],
  (enrichedIssues, filters, freeTextSearch, pnsFilter, miniIndex, user) => {
    return filterAllIssues({
      issues: enrichedIssues,
      filters,
      miniSearch: miniIndex,
      freeTextSearch,
      pnsFilter,
      currentUserId: user?._id || "",
    });
  },
);

export const selectIssuesWithTypeISSUE = createSelector([selectFilteredIssuesOnBoard], (issuesOnBoard): IIssueApi[] => {
  return issuesOnBoard.filter((issue) => issue.taskOrIssue === "ISSUE");
});

export const selectIssuesWithTypeTask = createSelector([selectFilteredIssuesOnBoard], (issuesOnBoard): IIssueApi[] => {
  return issuesOnBoard.filter((issue) => issue.taskOrIssue === "TASK");
});

export const selectIssueBoardBuckets = createSelector([selectHttpIssueBoard], (issueBoard: IIssueBoardApi | null) => {
  return sortArrayByNumber(issueBoard?.buckets ?? [], "order") || EMPTY_BUCKETS;
});

export const selectIssueBoardBucketNameSetting = createSelector(
  [selectHttpIssueBoard],
  (issueBoard: IIssueBoardApi | null): string => {
    return issueBoard?.bucketNameSetting || "Bucket";
  },
);

export const selectTypeAheadPNSItems = createSelector(
  [(state: IRootState) => state.adminReducer.project],
  (project: IProjectDeprecated | null): string[] => {
    return (project?.pns || []).map((pns) => `${pns.props.pnsId} - ${pns.props.title}`);
  },
);

export const selectKanbanBoardColumns = createSelector(
  [selectFilteredIssuesOnBoard, selectKanbanGroupingView, selectIssuesWithTypeISSUE, selectIssueBoardBuckets],
  (issuesOnBoard, kanbanGroupingView, issuesWithTypeIssue, issueBoardBuckets): IKanbanColumn<IIssueApi>[] => {
    const issues = kanbanGroupingView === IssueKanbanGroupingViewEnum.BUCKET ? issuesWithTypeIssue : issuesOnBoard;
    return convertIssuesToKanbanBoardColumns(issues, kanbanGroupingView, issueBoardBuckets);
  },
);

export const selectNrOfFiltredIssues = createSelector(
  [selectFilteredIssuesOnBoard],
  (filteredIssues) => filteredIssues.length,
);

export const selectIssueBoard = (state: IRootState): IIssueBoardApi => {
  const currentIssueBoardId = state.issueReducerNew.currentIssueBoardId;
  const resp = issueBoardRTK.endpoints.getSingleIssueBoard.select({
    projectId: state.globalReducer.currentProjectId,
    issueBoardId: currentIssueBoardId,
  });
  return resp(state).data;
};

export const selectMathFormulaConfigDraft = (state: IRootState) => state.issueReducerNew.mathFormulaConfigDraft;

export const selectMathFormulaSupportedAttributes = createSelector(
  [selectIssueBoard, (_state: IRootState, currentAttributeId?: string) => currentAttributeId],
  (issueBoard, currentAttributeId) => {
    const otherAttributesConfig = (issueBoard?.otherAttributesConfig ?? {}) as NonNullable<
      IIssueBoardApi["otherAttributesConfig"]
    >;

    return Object.entries(otherAttributesConfig)
      .filter(
        ([key, value]) =>
          key !== currentAttributeId &&
          value.isActive === true &&
          (value.typeOfValue === "NUMBER" || value.typeOfValue === "DROPDOWN"),
      )
      .map(([key, value]) => ({
        key,
        label: value.label,
        typeOfValue: value.typeOfValue,
      }));
  },
);

export const selectMathFormulaConfigValidationError = createSelector(
  [
    selectMathFormulaConfigDraft,
    (state: IRootState, currentAttributeId?: string) => selectMathFormulaSupportedAttributes(state, currentAttributeId),
  ],
  (draft, supportedAttributes) => {
    const variables = supportedAttributes.map((attribute) => attribute.key);
    return validateMathFormulaExpression(draft?.expression ?? "", variables);
  },
);

export const selectIsMathFormulaConfigValid = createSelector(
  [
    selectMathFormulaConfigDraft,
    (state: IRootState, currentAttributeId?: string) =>
      selectMathFormulaConfigValidationError(state, currentAttributeId),
  ],
  (draft, validationError) => {
    return Boolean(draft?.expression?.trim()) && validationError == null;
  },
);

export const selectIssueRegisterNameOptionOnBoard = createSelector(
  [selectHttpIssueBoard],
  (issueboard: IIssueBoardApi | null): IIssueRegisterNameOption | null => {
    if (!issueboard) return null;
    return ISSUE_REGISTER_NAME_OPTIONS.find((option) => option.type === issueboard.issueNameSetting) ?? null;
  },
);

export const selectTaskRegisterNameOptionOnBoard = createSelector(
  [selectHttpIssueBoard],
  (issueboard: IIssueBoardApi | null): IIssueActionNameOption | null => {
    if (!issueboard) return null;
    return ISSUE_ACTION_NAME_OPTIONS.find((option) => option.singular === issueboard.taskNameSetting) ?? null;
  },
);

export const selectFiltredIssuesIds = createSelector([selectFilteredIssuesOnBoard], (filteredIssues): string[] => {
  return Array.from(new Set(filteredIssues.map((issue) => issue._id)));
});

const selectHttpIssueTableTemplates = createSelector(
  [(state: IRootState) => state, selectCurrentProjectId, selectIssueBoardId],
  (state, projectId, issueBoardId) => {
    if (!projectId || !issueBoardId) return [];
    const q = openapi.endpoints.getProjectByProjectIdTableTemplates.select({ projectId, issueboardId: issueBoardId })(
      state,
    );
    return q.data ?? [];
  },
);

export const selectActiveTableTemplate = createSelector(
  [selectHttpIssueTableTemplates, (state: IRootState) => state.issueReducerNew.activeTableTemplateId],
  (tableTemplates, activeTableTemplateId) => {
    if (!activeTableTemplateId) return null;
    return tableTemplates.find((template) => template._id === activeTableTemplateId) || null;
  },
);

export const selectStatusDistributionForChart = createSelector(
  [selectFilteredIssuesOnBoard],
  (filteredIssues): IAgChartDataItem[] => {
    // 1. Räkna antal per status
    const countMap = filteredIssues.reduce((acc, issue) => {
      const status = issue.status || "Ukjent"; // fallback om status saknas
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // 2. Skapa färg-lookup från ISSUE_STATES_OPTIONS
    const colorMap = Object.fromEntries(ISSUE_STATES_OPTIONS.map(({ value, color }) => [value, color]));

    // 3. Bygg chart-data i rätt ordning (respektive ISSUE_STATES_OPTIONS)
    return ISSUE_STATES_OPTIONS.map(({ value }) => ({
      name: value,
      y: countMap[value] || 0,
      color: colorMap[value] || STANDARD_COLORS.grey, // fallback-färg
    })).filter((item) => item.y > 0); // valfritt: ta bort statusar med 0
  },
);

// remove members where user has duplicate entries in project members
export const deduplicatedMembers = createSelector(
  [(state: IRootState) => state.adminReducer.project],
  (project: IProjectDeprecated | null): IProjectMemberApiItem[] => {
    if (!project) return [];

    const members: IProjectMemberApiItem[] = Array.isArray(project.members) ? project.members : [];
    const uniqueMembersMap = new Map<string, IProjectMemberApiItem>();

    members.forEach((member) => {
      const userId = member.user._id;

      if (!uniqueMembersMap.has(userId)) {
        uniqueMembersMap.set(userId, member);
      }
    });

    return Array.from(uniqueMembersMap.values());
  },
);

export const selectIssueBoardScopedMembers = createSelector(
  [(state: IRootState) => state.adminReducer.project, selectHttpIssueBoard],
  (project, issueBoard): IProjectMemberApiItem[] => {
    const allMembers = Array.isArray(project?.members) ? (project.members as IProjectMemberApiItem[]) : [];

    const uniqueMembersMap = new Map<string, IProjectMemberApiItem>();
    allMembers.forEach((member) => {
      const userId = member?.user?._id;
      if (userId != null && uniqueMembersMap.has(userId) === false) {
        uniqueMembersMap.set(userId, member);
      }
    });

    const members = Array.from(uniqueMembersMap.values());
    const access = issueBoard?.access;
    if (access == null || access.level == null || access.level === AccessLevelsEnum.PROJECT) {
      return members;
    }

    if (access.level === AccessLevelsEnum.GROUPS) {
      const allowedGroups = new Set(access.groups ?? []);
      return members.filter((member) => allowedGroups.has(member.group) || member.group === "ADMIN");
    }

    if (access.level === AccessLevelsEnum.USERS) {
      const allowedUserIds = new Set(
        (access.users ?? []).map((user) => (typeof user === "string" ? user : user?._id)).filter(Boolean),
      );

      return members.filter((member) => member.user?._id != null && allowedUserIds.has(member.user._id));
    }

    return members;
  },
);
